import { useEffect, useRef } from 'react';
import { App, Button, Divider, Drawer, Form, Space } from 'antd';
import type { FieldDefinitionDto, OrganizationListItem } from '@crm/shared';
import { RecordForm } from '../../components/RecordForm';
import { fromFormValues, toFormValues } from '../../components/DynamicField';
import { useCreateOrganization, useUpdateOrganization } from '../../features/organizations/api';
import { errorMessage } from '../../api/client';
import { AddressesInput, toAddressesPayload } from '../../components/Addresses';

interface Props {
  open: boolean;
  onClose: () => void;
  defs: FieldDefinitionDto[];
  organization?: OrganizationListItem | null;
  onSaved?: (o: OrganizationListItem) => void;
}

export function OrganizationDrawer({ open, onClose, defs, organization, onSaved }: Props) {
  const { message } = App.useApp();
  const [form] = Form.useForm();
  const create = useCreateOrganization();
  const update = useUpdateOrganization();

  const latestDefs = useRef(defs);
  latestDefs.current = defs;
  useEffect(() => {
    if (!open) return;
    form.resetFields();
    form.setFieldsValue(
      organization ? { ...toFormValues(latestDefs.current, organization as unknown as Record<string, unknown>), addresses: organization.addresses ?? [] } : { fields: {}, addresses: [] },
    );
  }, [open, organization?.id, form]); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async (values: Record<string, unknown>) => {
    const payload = fromFormValues(defs, values);
    payload.addresses = toAddressesPayload(values.addresses as never);
    try {
      const saved = organization
        ? await update.mutateAsync({ id: organization.id, ...payload })
        : await create.mutateAsync(payload as Parameters<typeof create.mutateAsync>[0]);
      message.success(organization ? 'Organization updated' : 'Organization created');
      onSaved?.(saved);
      onClose();
    } catch (e) {
      message.error(errorMessage(e));
    }
  };

  return (
    <Drawer
      title={organization ? 'Edit organization' : 'New organization'}
      open={open}
      onClose={onClose}
      size={640}
      destroyOnHidden
      extra={
        <Space>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="primary" onClick={() => form.submit()} loading={create.isPending || update.isPending}>Save</Button>
        </Space>
      }
    >
      <Form form={form} layout="vertical" onFinish={submit} requiredMark={false}>
        <RecordForm defs={defs} form={form} />
        <Divider titlePlacement="start">Addresses</Divider>
        <Form.Item name="addresses">
          <AddressesInput />
        </Form.Item>
      </Form>
    </Drawer>
  );
}
